'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingState, EmptyState } from '../ui'
import { BaseDashboard, baseDashboardClasses } from '../BaseDashboard'
import { SparkCard } from '../charts'
import { useScreenshotToken } from './useScreenshotToken'
import { apiGet, useCatalog } from './entriesApi'
import { CaseEntryDetailPage } from './CaseEntryDetailPage'
import { CampaignDetailPage } from './CampaignDetailPage'
import { buildDayRange, bucketByDayRange } from './CasesDashboardView'
import { useDashboardRange } from '../dashboardRange'
import { EntriesTable } from './EntriesTable'
import { useListReturn } from './useListReturn'
import { useDetailPath } from './useDetailPath'
import { DomainCandidateDetail } from './DomainCandidateDetail'
import type { EntryRow } from './entryTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'


// Unified Entries — the next evolution of Cases: same list → CaseEntryDetailPage
// drill-in shape (unmodified — a unified entry is still exactly one
// user_entry, same as a Case), but backed by unified_entries_flat instead of
// raw_entries_flat. That view folds in the entry's raw/processed/brand_campaign
// lifecycle state (source_type/stage) and, when the entry belongs to a
// campaign, that campaign's own identity/footprint — surfaced here as extra
// Stage/Campaign columns Cases has no equivalent of. See getUnifiedEntriesFlat's
// own comment on why pagination here needs two passes instead of Cases' one.
export function UnifiedEntriesDashboardView({
  theme,
  platformIds,
  sourceType,
  storageKey,
  isEditor,
}: {
  theme: ProcessedTheme
  platformIds?: number[]
  sourceType?: string
  storageKey: string
  isEditor?: boolean
}) {
  const { isDark, border, muted } = baseDashboardClasses(theme)

  const [entries, setEntries] = useState<EntryRow[]>([])
  const [total, setTotal] = useState<number | null>(null)
  // Server-paged at PAGE_SIZE (20) by /unifiedentries — same append-on-
  // "Load more" model as CasesDashboardView / DomainCandidatesDashboardView.
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The open entry is the URL's last segment (/ctt/unified/{user_entry_id}),
  // so an entry can be linked, reloaded and opened in a new tab. `selected`
  // stays state (onUpdated patches it); the effect below keeps it in step
  // with the address bar.
  const detail = useDetailPath()
  const [selected, setSelected] = useState<EntryRow | null>(null)
  // A deep link to an entry this scope cannot see (or that no longer exists).
  const [missingId, setMissingId] = useState<string | null>(null)
  // Drill-through to the derived campaign an entry belongs to. This list is
  // flat and sorted by recency, so a campaign's other members are scattered
  // through it (or on another page entirely) with nothing tying them
  // together — opening the campaign is what actually shows them side by side.
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null)
  // Drill-through from an auto-sourced entry to the Domain Candidates record
  // it was promoted from (CaseEntryDetailPage's Sourcing section) — the same
  // detail component the Domain Candidates dashboard opens, so the evidence
  // reads identically from either side.
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null)
  const { catalog } = useCatalog()
  // Coming back from an entry or its campaign: same scroll position, the row highlighted.
  const back = useListReturn()

  const { turnstile, getToken: getScreenshotToken } = useScreenshotToken()

  const platformIdsKey = platformIds && platformIds.length > 0 ? platformIds.join(',') : ''

  // Timespan on last_created_at, sent as since/until (YYYY-MM-DD, until
  // inclusive): the shared dashboard range, last 30 days unless this brand's
  // Unified Entries cookie remembers another choice.
  const { from: rangeFrom, to: rangeTo, setRange } = useDashboardRange(`unified-${storageKey}`)

  // Same day-bucketed sparkline Cases leads with, but pinned to the selected
  // date range (not the loaded rows) so the chart tracks the range picker:
  // 90 days of buckets when 90 is chosen, instead of the entries-derived
  // 30-day cap that made the chart look stuck at 30.
  const chartDayRange = useMemo(() => buildDayRange(entries, { from: rangeFrom, to: rangeTo }), [entries, rangeFrom, rangeTo])
  const entriesChartData = useMemo(() => bucketByDayRange(entries, chartDayRange), [entries, chartDayRange])

  // Free-text search, server-side (/unified?q=): message, sender, subject,
  // domain, beneficiary, entity, reference. The list is paged at 20, so a
  // local filter over the loaded rows found nothing for a domain or
  // beneficiary on any later page — the search has to run in the query.
  // Debounced so each keystroke is not a round trip (same shape as Domain
  // Candidates' search).
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query])

  const fetchEntries = useCallback(async (targetPage = 0, isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else if (targetPage === 0) setLoading(true)
    else setLoadingMore(true)
    setError(null)
    const platformParam = platformIdsKey ? `&platform_ids=${platformIdsKey}` : ''
    const sourceParam = sourceType ? `&source=${encodeURIComponent(sourceType)}` : ''
    const rangeParam = `&since=${rangeFrom}&until=${rangeTo}`
    const searchParam = debouncedQuery ? `&q=${encodeURIComponent(debouncedQuery)}` : ''
    const result = await apiGet<{ entries: EntryRow[]; hasMore: boolean; total: number }>(`/unified?page=${targetPage}${platformParam}${sourceParam}${rangeParam}${searchParam}`)
    if (result.success && result.data) {
      setEntries((prev) => (targetPage === 0 ? result.data!.entries : [...prev, ...result.data!.entries]))
      // Server-side count under the same platform/source filters — not
      // entries.length, which only reflects the pages loaded so far.
      setTotal(result.data.total)
      setHasMore(result.data.hasMore)
      setPage(targetPage)
    } else {
      setError(result.error ?? 'Failed to load data')
    }
    setLoading(false)
    setRefreshing(false)
    setLoadingMore(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformIdsKey, sourceType, rangeFrom, rangeTo, debouncedQuery])

  // Initial load and every scope/range/search change refetch page 0. The
  // full-page spinner only on first load: a search refetch keeps the table
  // in place (refreshing) so the box keeps focus while typing.
  const firstLoadRef = useRef(true)
  useEffect(() => {
    fetchEntries(0, !firstLoadRef.current)
    firstLoadRef.current = false
  }, [fetchEntries])

  // Address bar -> open entry. A row click has already set `selected`; a cold
  // link (reload, new tab, pasted URL) resolves from the loaded page, else
  // asks /unifiedentries for that one row -- same view, mapping and tenant
  // scope as the list, and no date window, so an old entry opens too.
  const resolveSeq = useRef(0)
  useEffect(() => {
    const id = detail.id
    const seq = ++resolveSeq.current
    if (!id) { setSelected(null); return }
    if (selected?.id === id) return
    const loaded = entries.find((e) => e.id === id)
    if (loaded) { setSelected(loaded); return }
    if (loading) return   // the first page may still hold it
    const platformParam = platformIdsKey ? `&platform_ids=${platformIdsKey}` : ''
    apiGet<{ entries: EntryRow[] }>(`/unified?user_entry_id=${id}${platformParam}`).then((result) => {
      if (seq !== resolveSeq.current) return
      const row = result.success ? result.data?.entries?.[0] : undefined
      if (row) { setSelected(row); setMissingId(null) }
      else { setMissingId(id); detail.close() }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.id, entries, loading, platformIdsKey])

  if (selectedCandidateId) {
    return (
      <DomainCandidateDetail
        candidateId={selectedCandidateId}
        theme={theme}
        isEditor={isEditor}
        onClose={() => setSelectedCandidateId(null)}
        onChanged={() => fetchEntries(0, true)}
        onOpenCampaign={(campaignId) => { setSelectedCandidateId(null); setSelectedCampaignId(campaignId) }}
      />
    )
  }

  if (selectedCampaignId) {
    return (
      <CampaignDetailPage
        campaignId={selectedCampaignId}
        theme={theme}
        storageKey={storageKey}
        isEditor={isEditor}
        onClose={() => setSelectedCampaignId(null)}
      />
    )
  }

  return (
    <>
      {turnstile}
      {selected ? (
        <CaseEntryDetailPage
          entry={selected}
          theme={theme}
          catalog={catalog}
          onClose={detail.close}
          getScreenshotToken={getScreenshotToken}
          isEditor={isEditor}
          tenantCode={storageKey}
          onSelectCampaign={(campaignId) => { detail.close(); setSelectedCampaignId(campaignId) }}
          onOpenCandidate={(candidateId) => setSelectedCandidateId(candidateId)}
          onDeleted={(userEntryId) => {
            setEntries((prev) => prev.filter((e) => e.id !== userEntryId))
            setTotal((prev) => (prev != null ? prev - 1 : prev))
          }}
          onUpdated={(userEntryId, patch) => {
            setEntries((prev) => prev.map((e) => (e.id === userEntryId ? { ...e, ...patch } : e)))
            setSelected((prev) => (prev && prev.id === userEntryId ? { ...prev, ...patch } : prev))
          }}
        />
      ) : (
        <BaseDashboard
          rootRef={back.restoreRef}
          theme={theme}
          eyebrow="Moderation · Unified Entries"
          title="Unified Entries"
          subtitle="Every entry across its raw/processed/campaign lifecycle in one place — the next evolution of Cases (Old Cases and Conversation Cases sit under it)."
          infoContent={
            <>
              <p><span className="font-semibold">unified_entries_flat</span> — one row per user_entry, merging raw_entries/processed_entries/brand_campaigns (source_type reports the current stage) plus its campaign's identity and footprint, when it belongs to one.</p>
              <p className="mt-1.5">Same moderation actions as Cases (accept/reject/classify) — a unified entry is still exactly one user_entry underneath.</p>
            </>
          }
          onRefresh={() => fetchEntries(0, true)}
          refreshing={refreshing}
          loading={loading}
          error={error}
          onRetry={() => fetchEntries(0, true)}
          storageKey={`unified-${storageKey}-header`}
          timeRange={{ from: rangeFrom, to: rangeTo, onApply: setRange }}
          widgets={
            // Same SparkCard row as Cases: the number is the exact server-side
            // count for this platform/source scope and timespan, the sparkline a day-bucketing
            // of the pages loaded so far.
            <SparkCard
              id="unified-total"
              label="Number of entries"
              value={total ?? entries.length}
              chartData={entriesChartData}
              series={[{ key: 'count', color: 'var(--signal)' }]}
              isDark={isDark}
              className="h-28"
            />
          }
          widgetCols={4}
          widgetRows="auto"
        >
          <EntriesTable
            showIdColumns
            entries={entries}
            theme={theme}
            catalog={catalog}
            total={total}
            query={query}
            onQueryChange={setQuery}
            // A row that is one of several reports of the same thing opens
            // the campaign record rather than its own entry page: on its own
            // an entry shows one reporter's message and none of the others,
            // which is exactly the partial view a merged campaign exists to
            // replace. Nothing is lost — the campaign lists every report and
            // each one still opens its own raw/processed page from there.
            // Single-report entries (the large majority) open directly, since
            // for those the campaign would just be the same entry again.
            lastOpenedId={back.lastOpenedId}
            entryHref={(entry) => detail.href(entry.id)}
            extraToolbar={missingId && (
              <span className={`font-mono text-[10px] ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                Entry {missingId.slice(0, 8)} not found in this scope
                <button type="button" className={`ml-2 underline ${muted}`} onClick={() => setMissingId(null)}>dismiss</button>
              </span>
            )}
            onSelectEntry={(entry, rowEl) => back.open(entry.id, rowEl ?? null, () => {
              const flat = (entry.processed_entries_flat ?? {}) as Record<string, any>
              if (flat.campaign_id && (flat.campaign_entry_count ?? 1) > 1) {
                // Campaigns are derived (no stable id), so they open in place
                setSelectedCampaignId(flat.campaign_id as string)
              } else {
                setMissingId(null)
                setSelected(entry)
                detail.open(entry.id)
              }
            })}
            onSelectCampaign={setSelectedCampaignId}
          />
          {hasMore && (
            <div className={`flex justify-center border-t px-4 py-3 ${border}`}>
              <Button variant="action" size="sm" onClick={() => fetchEntries(page + 1)} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Load more'}</Button>
            </div>
          )}
        </BaseDashboard>
      )}
    </>
  )
}
